import { useMemo } from 'react'
import { Chip, KanbanBoard, type KanbanItem, type KanbanStage } from '@/components/ui'
import { parseDateOnly } from '@/lib/utils/date-only'
import { odooUrl } from '@/lib/utils/odoo-url'
import { usePreferences } from '@/shell/stores/preferences'
import type { PoGroup } from '../utils/po-grouping'
import type { ShipConfirmationStage } from '../api/types'
import {
  BOARD_STAGES,
  STAGE_SHORT_LABELS,
  STAGE_VARIANT,
  transitionAllowed,
} from '../utils/ship-confirmation-stage-meta'

const MODULE_ID = 'po-ship-confirmation'

interface PoGroupKanbanItem extends KanbanItem {
  group: PoGroup
}

export interface ShipConfirmationKanbanProps {
  items: PoGroup[]
  selectedId?: string
  onSelect: (group: PoGroup) => void
  /**
   * Fired for every drag-triggered move. The `confirmed` target is
   * special: confirming requires evidence (date + source), so the caller
   * must open `ConfirmShipDateModal` (in bulk mode, since a card here is
   * a whole PO, not a single line) rather than transitioning immediately
   * — this callback fires regardless of target, the caller decides.
   */
  onAdvance: (group: PoGroup, target: ShipConfirmationStage) => void
}

function kanbanAccent(
  v: 'neutral' | 'success' | 'warning' | 'danger' | 'info',
): 'neutral' | 'success' | 'warning' | 'danger' {
  if (v === 'info') return 'neutral'
  return v
}

const STAGE_BY_ID: Record<ShipConfirmationStage, KanbanStage> = BOARD_STAGES.reduce(
  (acc, s) => {
    acc[s] = { id: s, label: STAGE_SHORT_LABELS[s], accent: kanbanAccent(STAGE_VARIANT[s]) }
    return acc
  },
  {} as Record<ShipConfirmationStage, KanbanStage>,
)

function composeStageOrder(userOrder: ReadonlyArray<string>): KanbanStage[] {
  const seen = new Set<string>()
  const out: KanbanStage[] = []
  for (const id of userOrder) {
    if (id in STAGE_BY_ID && !seen.has(id)) {
      out.push(STAGE_BY_ID[id as ShipConfirmationStage])
      seen.add(id)
    }
  }
  for (const s of BOARD_STAGES) {
    if (!seen.has(s)) {
      out.push(STAGE_BY_ID[s])
      seen.add(s)
    }
  }
  return out
}

/**
 * Inbound PO Shipping Confirmation Tracker — kanban view of the
 * po_shipment_confirmation table, grouped one card per PO (`PoGroup`).
 * The underlying rows are tracked per PO *line* (2026-08-26 rework), but
 * a PO always shows as a single card here — splitting it across N cards
 * lost the "this whole shipment" context and surprised operators.
 * Per-line detail/actions live in `PoShipmentDetailDrawer`, opened via
 * `onSelect`. Two active columns (terminals drop off the board by
 * default, per `BOARD_STAGES`).
 */
export function ShipConfirmationKanban({
  items,
  selectedId,
  onSelect,
  onAdvance,
}: ShipConfirmationKanbanProps) {
  const userOrder = usePreferences((s) => s.kanbanStageOrder[MODULE_ID]) ?? []
  const setKanbanStageOrder = usePreferences((s) => s.setKanbanStageOrder)

  const stages = useMemo(() => composeStageOrder(userOrder), [userOrder])

  const kanbanItems: PoGroupKanbanItem[] = useMemo(
    () =>
      items.map((group) => ({
        id: String(group.odoo_po_id),
        stageId: group.stage,
        group,
      })),
    [items],
  )

  return (
    <KanbanBoard<PoGroupKanbanItem>
      stages={stages}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={(from, to) =>
        transitionAllowed(from as ShipConfirmationStage, to as ShipConfirmationStage)
      }
      onAdvance={(item, _from, to) => onAdvance(item.group, to as ShipConfirmationStage)}
      onSelect={(item) => onSelect(item.group)}
      onStageReorder={(next) => setKanbanStageOrder(MODULE_ID, next)}
      renderCard={(item) => <PoGroupKanbanCard group={item.group} />}
      scrollRestorationKey="po-ship-confirmation"
    />
  )
}

function PoGroupKanbanCard({ group }: { group: PoGroup }) {
  return (
    <div className="flex flex-col gap-1.5">
      <a
        href={odooUrl('purchase.order', group.odoo_po_id)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-role-data-primary truncate text-accent-text hover:underline"
      >
        {group.odoo_po_name}
      </a>
      {group.vendor_name ? (
        <div className="text-role-data-secondary truncate">{group.vendor_name}</div>
      ) : null}
      <div className="text-role-data-secondary truncate">
        {group.totalCount} {group.totalCount === 1 ? 'line' : 'lines'}
      </div>
      <StatusLine group={group} />
      <EstimateLoggedChip group={group} />
      <div className="text-role-metadata text-tertiary">Open {daysOpen(group.earliestCreatedAt)}d</div>
    </div>
  )
}

/** Blue chip when someone has logged an estimate on any still-awaiting
 *  line — tells a human estimate apart from the Odoo PO date. */
function EstimateLoggedChip({ group }: { group: PoGroup }) {
  const logged = group.lines.filter(
    (l) => l.stage === 'awaiting_confirmation' && (l.estimate_count ?? 0) > 0,
  ).length
  if (logged === 0) return null
  const awaiting = group.lines.filter((l) => l.stage === 'awaiting_confirmation').length
  return (
    <div>
      <Chip size="xs" variant="info">
        {awaiting > 1 ? `Estimated Ship Date Logged ${logged}/${awaiting}` : 'Estimated Ship Date Logged'}
      </Chip>
    </div>
  )
}

function StatusLine({ group }: { group: PoGroup }) {
  if (group.stage === 'confirmed') {
    const dates = new Set(group.lines.map((l) => l.confirmed_ship_date).filter(Boolean))
    if (dates.size === 1) {
      return (
        <div className="text-role-metadata">Confirmed for {formatDate([...dates][0] as string)}</div>
      )
    }
    return <div className="text-role-metadata">All {group.totalCount} lines confirmed</div>
  }

  if (group.confirmedCount > 0) {
    return (
      <div className="text-role-metadata">
        {group.confirmedCount}/{group.totalCount} lines confirmed
      </div>
    )
  }

  const estimates = group.lines
    .map((l) => l.latest_estimated_date)
    .filter((d): d is string => !!d)
    .sort()
  if (estimates.length > 0) {
    return (
      <div className="text-role-metadata italic text-tertiary">
        Estimate only: {formatDate(estimates[0])}
      </div>
    )
  }
  return <div className="text-role-metadata italic text-tertiary">No date on file</div>
}

function formatDate(iso: string | null | undefined): string {
  // confirmed_ship_date / latest_estimated_date are date-only ("YYYY-MM-DD")
  // — parseDateOnly avoids the classic new Date(dateOnlyString) UTC-midnight
  // shift that displays the prior calendar day in any US timezone.
  if (!iso) return '—'
  const d = parseDateOnly(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function daysOpen(iso: string): number {
  const created = new Date(iso).getTime()
  if (Number.isNaN(created)) return 0
  return Math.max(0, Math.floor((Date.now() - created) / 86_400_000))
}
